import React, { useCallback, useEffect, useMemo, useState } from 'react'
import MarkSelect from './MarkSelect'
import ModelSelect from './ModelSelect'
import ModSelect from './ModSelect'
import { SelectOption } from './hooks/useSelectInput'
import { catalogClient } from '../services/grpc'
import { TireSizeReq, YearsReq } from '../generated/catalog_pb'
import '../styles/car-filter.css'

export type TireSizeSelection = {
  width: string
  height: string
  diameter: string
}

interface CarFilterProps {
  onTireSizeSelect?: (size: TireSizeSelection | null) => void
  currentWidth?: string
  currentProfile?: string
  currentDiameter?: string
}

const CarFilter: React.FC<CarFilterProps> = ({
  onTireSizeSelect,
  currentWidth,
  currentProfile,
  currentDiameter,
}) => {
  const [selectedMark, setSelectedMark] = useState<SelectOption | null>(null)
  const [selectedModel, setSelectedModel] = useState<SelectOption | null>(null)
  const [selectedMod, setSelectedMod] = useState<SelectOption | null>(null)
  const [year, setYear] = useState<number | ''>('')
  const [availableYears, setAvailableYears] = useState<number[]>([])
  const [isYearsLoading, setIsYearsLoading] = useState(false)
  const [yearsError, setYearsError] = useState<string | null>(null)
  const [tireSizes, setTireSizes] = useState<TireSizeSelection[]>([])
  const [isTireSizeLoading, setIsTireSizeLoading] = useState(false)
  const [tireSizeError, setTireSizeError] = useState<string | null>(null)
  const [selectedTireSizeKey, setSelectedTireSizeKey] = useState('')

  const resetTireSizes = useCallback(() => {
    setTireSizes([])
    setSelectedTireSizeKey('')
    setTireSizeError(null)
    setIsTireSizeLoading(false)
  }, [])

  const handleMarkSelect = useCallback((mark: SelectOption | null) => {
    setSelectedMark(mark)
    setSelectedModel(null)
    setSelectedMod(null)
    setYear('')
    setAvailableYears([])
    setIsYearsLoading(false)
    setYearsError(null)
    resetTireSizes()
  }, [resetTireSizes])

  const handleModelSelect = useCallback((model: SelectOption | null) => {
    setSelectedModel(model)
    setSelectedMod(null)
    setYear('')
    setAvailableYears([])
    setIsYearsLoading(false)
    setYearsError(null)
    resetTireSizes()
  }, [resetTireSizes])

  useEffect(() => {
    if (!selectedModel) {
      setAvailableYears([])
      setIsYearsLoading(false)
      setYearsError(null)
      setYear('')
      setSelectedMod(null)
      resetTireSizes()
      return
    }

    const modelId = Number(selectedModel.id)
    if (Number.isNaN(modelId)) {
      setAvailableYears([])
      setYearsError('Не удалось определить модель')
      setIsYearsLoading(false)
      setYear('')
      setSelectedMod(null)
      resetTireSizes()
      return
    }

    let isActive = true

    setIsYearsLoading(true)
    setYearsError(null)
    setAvailableYears([])

    const request = new YearsReq()
    request.setModelId(modelId)

    const stream = catalogClient.yearRequest(request, {}, (err, response) => {
      if (!isActive) {
        return
      }

      if (err) {
        console.error('Ошибка загрузки годов:', err)
        setYearsError('Не удалось загрузить года')
        setAvailableYears([])
      } else {
        const yearsList = response?.getYearList() ?? []
             let years=[];
        for (let i in yearsList)
        {
          let year=Number(yearsList[i]);
          if (Number.isNaN(year)){continue;}
          years.push(year)
        }
        const sortedYears = [...years].sort((a, b) => b - a)
        setAvailableYears(sortedYears)
        setYearsError(null)
      }

      setIsYearsLoading(false)
    })

    return () => {
      isActive = false
      if (stream && typeof stream.cancel === 'function') {
        stream.cancel()
      }
    }
  }, [selectedModel, resetTireSizes])

  useEffect(() => {
    if (year !== '' && !availableYears.includes(year)) {
      setYear('')
      setSelectedMod(null)
      resetTireSizes()
    }
  }, [availableYears, year, resetTireSizes])

  const handleYearChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const nextValue = event.target.value
      if (!nextValue) {
        setYear('')
        setSelectedMod(null)
        resetTireSizes()
        return
      }

      const parsedYear = Number(nextValue)
      setYear(Number.isNaN(parsedYear) ? '' : parsedYear)
      setSelectedMod(null)
      resetTireSizes()
    },
    [resetTireSizes]
  )

  const handleModSelect = useCallback((mod: SelectOption | null) => {
    setSelectedMod(mod)
    resetTireSizes()
  }, [resetTireSizes])

  useEffect(() => {
    if (!selectedMod) {
      resetTireSizes()
      return
    }

    const modId = Number(selectedMod.id)
    if (Number.isNaN(modId)) {
      setTireSizeError('Не удалось определить модификацию')
      setTireSizes([])
      setIsTireSizeLoading(false)
      return
    }

    let isActive = true
    setIsTireSizeLoading(true)
    setTireSizeError(null)

    const request = new TireSizeReq()
    request.setModId(modId)

    catalogClient.sizeRequest(request, {}, (err, response) => {
      if (!isActive) {
        return
      }

      if (err) {
        console.error('Ошибка загрузки размеров:', err)
        setTireSizeError('Не удалось загрузить размеры')
        setTireSizes([])
      } else {
        const sizeList = response?.getSizeList() ?? []
        const uniqueSizes: TireSizeSelection[] = []
        const seen = new Set<string>()

        sizeList.forEach((item) => {
          const size: TireSizeSelection = {
            width: String(item.getProp1()),
            height: String(item.getProp2()),
            diameter: String(item.getProp3()),
          }
          const key = `${size.width}/${size.height}/${size.diameter}`
          if (!seen.has(key)) {
            seen.add(key)
            uniqueSizes.push(size)
          }
        })

        setTireSizes(uniqueSizes)
      }

      setIsTireSizeLoading(false)
    })

    return () => {
      isActive = false
    }
  }, [selectedMod, resetTireSizes])

  useEffect(() => {
    if (!currentWidth || !currentProfile || !currentDiameter) {
      setSelectedTireSizeKey('')
      return
    }

    if (tireSizes.length === 0) {
      setSelectedTireSizeKey('')
      return
    }

    const currentKey = `${currentWidth}/${currentProfile}/${currentDiameter}`
    const exists = tireSizes.some(
      (size) => `${size.width}/${size.height}/${size.diameter}` === currentKey
    )

    setSelectedTireSizeKey(exists ? currentKey : '')
  }, [currentWidth, currentProfile, currentDiameter, tireSizes])

  const handleTireSizeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const key = event.target.value
      setSelectedTireSizeKey(key)

      if (!key) {
        if (onTireSizeSelect) {
          onTireSizeSelect(null)
        }
        return
      }

      const selectedSize = tireSizes.find(
        (size) => `${size.width}/${size.height}/${size.diameter}` === key
      )

      if (selectedSize && onTireSizeSelect) {
        onTireSizeSelect(selectedSize)
      }
    },
    [onTireSizeSelect, tireSizes]
  )

  const markId = selectedMark ? Number(selectedMark.id) : null
  const modelId = selectedModel ? Number(selectedModel.id) : null
  const numericYear = year === '' ? null : year
  const isYearSelectDisabled =
    !selectedModel || isYearsLoading || availableYears.length === 0
  const yearPlaceholder = !selectedModel
    ? 'Сначала выберите модель'
    : isYearsLoading
    ? 'Загрузка...'
    : availableYears.length === 0
    ? 'Нет доступных годов'
    : 'Выберите год'
  const showNoYearsMessage =
    !isYearsLoading && !yearsError && selectedModel && availableYears.length === 0

  const sizePlaceholder = !selectedMod
    ? 'Сначала выберите модификацию'
    : isTireSizeLoading
    ? 'Загрузка размеров...'
    : tireSizes.length === 0
    ? 'Размеры не найдены'
    : 'Выберите размер'

  const selectedSize = useMemo(
    () =>
      selectedTireSizeKey
        ? tireSizes.find(
            (size) => `${size.width}/${size.height}/${size.diameter}` === selectedTireSizeKey
          )
        : null,
    [selectedTireSizeKey, tireSizes]
  )

  return (
    <div className="car-filter">
      <div className="car-filter__field">
        <label className="car-filter__label" htmlFor="car-filter-mark">
          Марка
        </label>
        <MarkSelect onMarkSelect={handleMarkSelect} inputId="car-filter-mark" />
      </div>

      <div className="car-filter__field">
        <label className="car-filter__label" htmlFor="car-filter-model">
          Модель
        </label>
        <ModelSelect
          markId={markId}
          onModelSelect={handleModelSelect}
          inputId="car-filter-model"
        />
      </div>

      <div className="car-filter__field">
        <label className="car-filter__label" htmlFor="car-filter-year">
          Год выпуска
        </label>
        <select
          id="car-filter-year"
          className="car-filter__select"
          value={year === '' ? '' : String(year)}
          onChange={handleYearChange}
          disabled={isYearSelectDisabled}
        >
          <option value="">{yearPlaceholder}</option>
          {availableYears.map((yearOption) => (
            <option key={yearOption} value={yearOption}>
              {yearOption}
            </option>
          ))}
        </select>
        {isYearsLoading && (
          <div className="car-filter__status">Загрузка годов...</div>
        )}
        {yearsError && (
          <div className="car-filter__status car-filter__status--error">
            {yearsError}
          </div>
        )}
        {showNoYearsMessage && (
          <div className="car-filter__status">Доступные года не найдены</div>
        )}
      </div>

      <div className="car-filter__field">
        <label className="car-filter__label" htmlFor="car-filter-mod">
          Модификация
        </label>
        <ModSelect
          modelId={modelId}
          year={numericYear}
          onModSelect={handleModSelect}
          inputId="car-filter-mod"
          disabled={!selectedModel || numericYear === null}
        />
      </div>

      <div className="car-filter__field">
        <label className="car-filter__label" htmlFor="car-filter-tire-size">
          Размер шины
        </label>
        <select
          id="car-filter-tire-size"
          className="car-filter__select"
          value={selectedTireSizeKey}
          onChange={handleTireSizeChange}
          disabled={!selectedMod || isTireSizeLoading || tireSizes.length === 0}
        >
          <option value="">{sizePlaceholder}</option>
          {tireSizes.map((size) => {
            const key = `${size.width}/${size.height}/${size.diameter}`
            return (
              <option key={key} value={key}>
                {`${size.width}/${size.height} R${size.diameter}`}
              </option>
            )
          })}
        </select>
        {isTireSizeLoading && (
          <div className="car-filter__status">Загрузка размеров...</div>
        )}
        {tireSizeError && (
          <div className="car-filter__status car-filter__status--error">
            {tireSizeError}
          </div>
        )}
        {!isTireSizeLoading && !tireSizeError && selectedMod && tireSizes.length === 0 && (
          <div className="car-filter__status">Размеры не найдены</div>
        )}
      </div>

      {selectedMark && selectedModel && selectedMod && numericYear && (
        <div className="car-filter__summary">
          Подбор: {selectedMark.label} · {selectedModel.label} · {numericYear} · {selectedMod.label}
          {selectedSize && (
            <>
              {' '}
              · {selectedSize.width}/{selectedSize.height} R{selectedSize.diameter}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default CarFilter